/**
 * LES DEUX CONTRÔLES DE SAISIE D'UNE ATTESTATION — fonctions pures.
 *
 * ⚠️ ILS SONT ICI, ET NON DANS L'ÉCRAN, POUR QU'UN ESSAI PUISSE LES IMPORTER.
 * `tsconfig.tests.json` ne configure pas `--jsx` : importer un `.tsx` depuis un
 * essai passe au vert sous `vitest` (esbuild transpile sans typer) et casse le
 * typecheck du pré-vol — donc au moment de déployer. Un essai vert qui casse la
 * bascule est pire qu'un essai rouge.
 *
 * ⚠️ ET CE NE SONT PAS DES CONTRÔLES DE CONFORT : l'ancrage d'une attestation
 * devient IMMUABLE à la création. Une erreur ne se corrige pas, elle s'annule
 * et se recommence.
 */

/**
 * ⚠️ BORNES ARRONDIES VERS L'EXTÉRIEUR, DÉLIBÉRÉMENT. Un contrôle trop serré
 * ferait refuser un acte légitime — ce qui est pire qu'un contrôle absent :
 * l'agent apprendrait à contourner l'outil.
 */
export const BORNES_GN = { latMin: 7.0, latMax: 12.8, lonMin: -15.2, lonMax: -7.5 };

/**
 * Le point est-il hors de Guinée ? Rend le motif, ou `null` si rien à dire.
 *
 * ⚠️ LE CAS QUI MOTIVE CE CONTRÔLE EST LA FAUTE LA PLUS BANALE QUI SOIT : un
 * signe moins oublié sur la longitude. `13.6785` au lieu de `-13.6785` place le
 * terrain à 3 000 km, dans le golfe de Guinée. Rien ne l'objectait.
 */
export function horsDuPays(lat: string, lon: string): string | null {
  const la = Number(lat), lo = Number(lon);
  // ⚠️ ON NE SE PRONONCE PAS SUR UNE SAISIE INCOMPLÈTE : un message qui crie
  // dès la première frappe s'apprend à être ignoré avant d'avoir servi.
  if (!lat.trim() || !lon.trim() || Number.isNaN(la) || Number.isNaN(lo)) return null;
  if (la < BORNES_GN.latMin || la > BORNES_GN.latMax || lo < BORNES_GN.lonMin || lo > BORNES_GN.lonMax) {
    const inverse = lo > 0 && -lo >= BORNES_GN.lonMin && -lo <= BORNES_GN.lonMax;
    return inverse
      ? `Ce point est hors de Guinée. La longitude est positive : en Guinée elle est NÉGATIVE — vouliez-vous ${-lo} ?`
      : `Ce point (${la}, ${lo}) est hors de Guinée. L'ancrage est immuable après création.`;
  }
  return null;
}

/**
 * Les côtés contredisent-ils la superficie déclarée ?
 *
 * ⚠️ ON SIGNALE, ON N'IMPOSE PAS — un terrain n'est pas toujours rectangulaire.
 * Mais on pouvait écrire 10 m × 10 m et 500 m² dans le même acte, et le papier
 * sort avec les trois chiffres : c'est lui qui fait foi devant un tiers.
 */
export function dimensionsIncoherentes(lg: string, la: string, sup: string): string | null {
  const L = Number(lg), l = Number(la), S = Number(sup);
  if (!lg.trim() || !la.trim() || !sup.trim()) return null;
  if (![L, l, S].every((n) => Number.isFinite(n) && n > 0)) return null;
  const calculee = L * l;
  const ecart = Math.abs(calculee - S) / S;
  if (ecart <= 0.1) return null;
  return `${L} m × ${l} m donne ${calculee.toLocaleString('fr-FR')} m², `
    + `et vous déclarez ${S.toLocaleString('fr-FR')} m² — ${Math.round(ecart * 100)} % d'écart. `
    + `Si le terrain n'est pas rectangulaire c'est normal ; sinon l'un des trois chiffres est faux.`;
}
